import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import styles from "./style/EnterpriseList.module.scss";
import Button from "../Utilities/Button";
import TextField from "../Utilities/TextField";
import Checkbox from "../Utilities/Checkbox";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import Alert from "../Utilities/Alert";
import { openTrialPlan, openPersonalTrial } from "../../store/slice/manager/enterpriseCompany";
import { getPlanPackagesPublishing } from "../../store/slice/manager/plans";
import { selectMenu } from "../../store/slice/manager/menu";
import { getServiceName } from "../../constant/SERVICE_NAME_MAP";
import { getPackageBillingType } from "../../constant/SERVICE_PACKAGE_TYPES";
import { resolveOpenMode } from "./trialOpenMode";
import { toChineseNumber } from "../../utils";
import { AiOutlineSetting } from "react-icons/ai";
import { BiTrashAlt } from "react-icons/bi";

const serviceLabel = (data, menu) => {
  const menuItem = (menu || []).find(
    (m) => m.id === data?.software_services_id
  );
  return getServiceName(
    menuItem?.name ||
      data?.software_services_name ||
      data?.service_name ||
      data?.name ||
      menuItem?.name_local
  );
};

// 方案下拉顯示：試用方案免費，付費方案以 real_rental_fee（已含折扣）× 付費週期單位呈現
const planFeeLabel = (plan) => {
  if (!plan) return "";
  if (plan.is_trial) return "免費試用";
  const unit = plan.pay_type === "month" ? "每人/月" : "每人/年";
  const amount = Number(plan.real_rental_fee ?? 0).toLocaleString("en-US");
  return `NT$${amount} ${unit}`;
};

const planOptionLabel = (plan) => {
  const fee = planFeeLabel(plan);
  return fee ? `${plan.name}（${fee}）` : plan.name;
};

// 席位（stored_value）面額下拉：顯示方案名稱與面額金額（rental_fee 即該面額價格）
const seatPlanLabel = (plan) => {
  const amount = Number(plan?.rental_fee ?? plan?.real_rental_fee ?? 0).toLocaleString("en-US");
  return `${plan.name}（NT$${amount}）`;
};

// variant='personal' 時改打個人端點（openPersonalTrial，帶 userId、不扣款）；
// 個人固定 1 人，不顯示人數上限；預設維持企業行為。
const TrialPlanPanel = ({
  data,
  enterpriseId,
  userId,
  variant = "enterprise",
  restart,
  onClose,
  readOnly = false,
  onSeatRowsChange,
}) => {
  const dispatch = useDispatch();
  const { menu } = useSelector(selectMenu);
  const isPersonal = variant === "personal";
  const [openMode, setOpenMode] = useState(() => resolveOpenMode(data)); // 'trial' | 'subscription' | null
  const [dateError, setDateError] = useState(false);
  const [maxUsers, setMaxUsers] = useState(data.users_count_upper_limit || "");
  const [planDate, setPlanDate] = useState({
    startDay: data.start_date,
    endDay: data.expiration_date,
  });
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // 可選方案（該服務所有上架方案的葉節點），與目前選定的 plan_id
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(data?.plan?.id ?? "");
  // 席位方案（stored_value）開通：多筆「面額 + 席位人數」
  const [seatRows, setSeatRows] = useState([{ plan_id: "", number_of_person: "" }]);
  const seatHydrateKeyRef = useRef(null);
  const onSeatRowsChangeRef = useRef(onSeatRowsChange);

  useEffect(() => {
    onSeatRowsChangeRef.current = onSeatRowsChange;
  }, [onSeatRowsChange]);

  const serviceId = data?.software_services_id;

  useEffect(() => {
    setOpenMode(resolveOpenMode(data));
    setMaxUsers(data.users_count_upper_limit || "");
    setPlanDate({
      startDay: data.start_date,
      endDay: data.expiration_date,
    });
    setSelectedPlanId(data?.plan?.id ?? "");
    // 依賴刻意用細粒度欄位（涵蓋 resolveOpenMode 實際會讀的欄位），不依賴整個 data 物件以免引用變化過度重跑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.software_services_id,
    data?.activation_state,
    data?.activate,
    data?.subscription_status,
    data?.is_trial,
    data?.users_count_upper_limit,
    data?.start_date,
    data?.expiration_date,
    data?.plan?.id,
  ]);

  // 抓該服務的上架方案：訂閱(group_by_plan)與席位(stored_value)兩型別都取，攤平成葉節點方案後去重
  useEffect(() => {
    if (!serviceId) return;
    let alive = true;
    Promise.all([
      dispatch(getPlanPackagesPublishing({ service: serviceId, type: "group_by_plan" })),
      dispatch(getPlanPackagesPublishing({ service: serviceId, type: "stored_value" })),
    ]).then((results) => {
      if (!alive) return;
      const byId = new Map();
      results.forEach((res) => {
        const packages = res?.payload?.packages;
        (Array.isArray(packages) ? packages : []).forEach((pkg) => {
          const billing = getPackageBillingType(pkg);
          (Array.isArray(pkg?.plans) ? pkg.plans : []).forEach((plan) => {
            if (plan?.id != null && !byId.has(plan.id)) {
              byId.set(plan.id, { ...plan, _billing: billing });
            }
          });
        });
      });
      setPlans(Array.from(byId.values()));
    });
    return () => {
      alive = false;
    };
  }, [dispatch, serviceId]);

  // 開通試用與開通訂閱共用同一批上架方案：方案建立時一律 is_trial=false，
  // 後端也依 plan_id 授予、不限方案本身的 is_trial，故不再依模式過濾，
  // 避免某一模式（如開通訂閱）選不到方案。
  const modeIsTrial = openMode === "trial";
  const availablePlans = plans;

  // 席位方案（stored_value）面額清單：非試用的面額（每筆帶 rental_fee 面額價格）。
  // 有面額即進入席位模式：改用「多筆面額 + 席位人數」而非單一方案下拉。個人端不適用。
  const storedValuePlans = useMemo(
    () => plans.filter((p) => p._billing === "stored_value" && !p.is_trial),
    [plans]
  );
  const isSeatPlan = !isPersonal && storedValuePlans.length > 0;

  const handleSeatRowChange = (index, key, value) =>
    setSeatRows((rows) => rows.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  const addSeatRow = () =>
    setSeatRows((rows) => [
      ...rows,
      {
        plan_id: storedValuePlans[0]?.id != null ? String(storedValuePlans[0].id) : "",
        number_of_person: "",
      },
    ]);
  const removeSeatRow = (index) =>
    setSeatRows((rows) => (rows.length === 1 ? rows : rows.filter((_, i) => i !== index)));

  // 唯讀（付費訂閱）時目前方案可能來自未上架的方案包，補進選項以利顯示
  const optionPlans = useMemo(() => {
    const list = [...availablePlans];
    const current = data?.plan;
    if (current?.id != null && !list.some((p) => p.id === current.id)) {
      list.unshift(current);
    }
    return list;
  }, [availablePlans, data?.plan]);

  // 切換模式或方案載入後，確保選定值落在可選清單內（優先沿用既有 grant 的方案）
  useEffect(() => {
    if (!openMode) return;
    setSelectedPlanId((prev) => {
      if (prev && availablePlans.some((p) => p.id === prev)) return prev;
      if (data?.plan?.id && availablePlans.some((p) => p.id === data.plan.id)) {
        return data.plan.id;
      }
      return availablePlans[0]?.id ?? "";
    });
  }, [openMode, availablePlans, data?.plan?.id]);

  // 面額載入後帶入 seat_plans；僅在服務或後端明細變更時 hydrate，避免編輯中被覆寫
  const seatPlansHydrateKey = `${data?.software_services_id ?? ""}:${JSON.stringify(
    data?.seat_plans ?? []
  )}`;

  useEffect(() => {
    if (!isSeatPlan || storedValuePlans.length === 0) return;
    if (seatHydrateKeyRef.current === seatPlansHydrateKey) return;
    seatHydrateKeyRef.current = seatPlansHydrateKey;

    if (Array.isArray(data?.seat_plans) && data.seat_plans.length > 0) {
      setSeatRows(
        data.seat_plans.map((r) => ({
          plan_id: r.plan_id != null ? String(r.plan_id) : "",
          number_of_person:
            r.number_of_person != null && r.number_of_person !== ""
              ? String(r.number_of_person)
              : "",
        }))
      );
      return;
    }
    setSeatRows([
      {
        plan_id: storedValuePlans[0]?.id != null ? String(storedValuePlans[0].id) : "",
        number_of_person: "",
      },
    ]);
  }, [isSeatPlan, storedValuePlans, seatPlansHydrateKey, data?.seat_plans]);

  // 編輯中的席次同步到卡片上方摘要（僅顯示已填寫面額與人數的列）
  useEffect(() => {
    if (!onSeatRowsChangeRef.current || !isSeatPlan) return;
    onSeatRowsChangeRef.current(
      seatRows
        .filter((r) => r.plan_id && Number(r.number_of_person) > 0)
        .map((r) => {
          const plan = storedValuePlans.find((p) => String(p.id) === String(r.plan_id));
          return {
            plan_id: Number(r.plan_id),
            plan_name: plan?.name ?? "",
            rental_fee: Number(plan?.rental_fee ?? plan?.real_rental_fee ?? 0),
            number_of_person: Number(r.number_of_person),
          };
        })
    );
  }, [seatRows, isSeatPlan, storedValuePlans]);

  const handleStartDay = (date) => {
    const startDay = new Date(date);
    return format(startDay.setDate(startDay.getDate() - 1), "yyyy-MM-dd");
  };

  const handleDay = () => {
    const startDay = new Date(planDate.startDay);
    const endDay = new Date(planDate.endDay);
    const daysDiffA =
      Math.floor((+endDay - +startDay) / (1000 * 60 * 60 * 24)) + 1;
    return daysDiffA > 0 ? daysDiffA : "-";
  };

  const handleDate = () => {
    const startDay = new Date(planDate.startDay);
    const endDay = new Date(planDate.endDay);
    endDay.setHours(0, 0, 0, 0);
    startDay.setHours(0, 0, 0, 0);
    return startDay.getTime() < endDay.getTime();
  };

  const toggleMode = (mode) => {
    if (readOnly) return;
    setOpenMode((prev) => (prev === mode ? null : mode));
  };

  const handleComplete = () => {
    // 席位模式改用每筆面額的席位人數，不吃單一人數上限。
    if (!isPersonal && !isSeatPlan && !(maxUsers > 0)) {
      setError(true);
      setErrorMsg("請輸入正確的人數上限");
      return;
    }
    if (!planDate.startDay || !planDate.endDay) {
      setError(true);
      setDateError(true);
      setErrorMsg("請選擇起訖日期");
      return;
    }
    if (!(handleDay() > 0)) {
      setDateError(true);
      setErrorMsg("起始日需早於到期日");
      return;
    }
    const toDay = new Date();
    const endDay = new Date(planDate.endDay);
    toDay.setHours(0, 0, 0, 0);
    endDay.setHours(0, 0, 0, 0);
    if (endDay.getTime() < toDay.getTime()) {
      setDateError(true);
      setErrorMsg("到期日不可早於今天");
      return;
    }
    if (!openMode) {
      setError(true);
      setErrorMsg("請選擇開通試用或開通訂閱");
      return;
    }
    if (isSeatPlan) {
      const invalid = seatRows.some(
        (r) => !r.plan_id || !(Number(r.number_of_person) > 0)
      );
      if (!seatRows.length || invalid) {
        setError(true);
        setErrorMsg("請完整選擇面額並填寫席位人數");
        return;
      }
    } else if (availablePlans.length > 0 && !selectedPlanId) {
      setError(true);
      setErrorMsg("請選擇方案");
      return;
    }

    setError(false);
    setDateError(false);
    setErrorMsg("");
    const base = {
      start_date: format(new Date(planDate.startDay), "yyyy-MM-dd"),
      expiration_date: format(new Date(planDate.endDay), "yyyy-MM-dd"),
      activate: true,
      is_trial: openMode === "trial",
      ...(isPersonal ? { user_id: userId } : { enterprise_id: enterpriseId }),
    };
    // 席位方案：送多筆 { plan_id（面額）, number_of_person（席位人數）}；
    // 一般方案維持單一 plan_id + users_count_upper_limit。
    const payload = isSeatPlan
      ? {
          ...base,
          service_id: data.software_services_id,
          plans: seatRows.map((r) => ({
            plan_id: Number(r.plan_id),
            number_of_person: Number(r.number_of_person),
          })),
        }
      : {
          ...base,
          // 個人帳號永遠只有一人；企業才採輸入值
          users_count_upper_limit: isPersonal ? 1 : +maxUsers,
          // 有選定方案就送 plan_id（後端據此授予指定方案）；
          // 後端遇到 service_id 會優先走預設方案，故有 plan_id 時不再帶 service_id。
          ...(selectedPlanId
            ? { plan_id: selectedPlanId }
            : { service_id: data.software_services_id }),
        };
    const action = isPersonal ? openPersonalTrial(payload) : openTrialPlan(payload);
    dispatch(action).then((e) => {
      if (e?.payload?.msg === "success") {
        restart && restart();
        onClose();
        return;
      }
      const apiErr = e?.payload?.data;
      const firstFieldErr =
        apiErr && typeof apiErr === "object"
          ? Object.values(apiErr).flat?.()?.[0]
          : null;
      setErrorMsg(
        (typeof apiErr === "string" && apiErr) ||
          firstFieldErr ||
          e?.payload?.message ||
          "儲存失敗，請確認設定後再試一次"
      );
    });
  };

  return (
    <>
      <div className={styles.trialPanel}>
        <div className={styles.trialPanelTitle}>
          <AiOutlineSetting /> 開通設定 — {serviceLabel(data, menu)}
          {readOnly && (
            <span className={styles.trialPanelReadOnlyTag}>唯讀</span>
          )}
        </div>

        {!isPersonal && !isSeatPlan && (
          <div className={styles.trialPanelField}>
            <label>人數上限</label>
            <TextField
              placeholder='請輸入'
              width='100%'
              numeric={true}
              value={maxUsers ? maxUsers : ""}
              onChange={(e) => setMaxUsers(e)}
              error={(!maxUsers || maxUsers <= 0) && error}
              min={1}
              isDisabled={readOnly}
            />
          </div>
        )}

        <div className={styles.trialPanelField}>
          <label>使用期間</label>
          <div className={styles.trialPanelDates}>
            <Datepicker
              selectDate={planDate?.startDay}
              setSelectDate={(e) => {
                if (readOnly) return;
                setDateError(false);
                setPlanDate({ ...planDate, startDay: e });
              }}
              disabled={readOnly}
              disableEvents={[
                {
                  // 不以「今天」為下限，允許回溯選擇較早的起始日（後台開通/試用可回溯）。
                  // 仍保留上限＝結束日，維持「起始日不得晚於結束日」的約束。
                  start: "2000-01-01",
                  end: `${
                    planDate?.endDay && handleDate()
                      ? planDate?.endDay
                      : "2100-12-31"
                  }`,
                },
              ]}
              error={(error && !planDate?.startDay) || dateError}
            />
            <span>至</span>
            <Datepicker
              selectDate={planDate?.endDay}
              setSelectDate={(e) => {
                if (readOnly) return;
                setDateError(false);
                setPlanDate({ ...planDate, endDay: e });
              }}
              disabled={readOnly}
              disableEvents={[
                {
                  start: `${
                    planDate?.startDay
                      ? handleStartDay(planDate?.startDay)
                      : handleStartDay(new Date())
                  }`,
                  end: `2100-12-31`,
                },
              ]}
              error={(error && !planDate?.endDay) || dateError}
            />
          </div>
          <div className={styles.trialPanelDays}>使用天數為 {handleDay()}</div>
        </div>

        <div className={styles.trialPanelCheckboxes}>
          <div className={styles.trialPanelCheckboxItem}>
            <Checkbox
              value={openMode === "trial"}
              onClick={() => toggleMode("trial")}
              isDisable={readOnly}
              color="#5856D6"
            />
            <span onClick={() => toggleMode("trial")}>開通試用</span>
          </div>
          <div className={styles.trialPanelCheckboxItem}>
            <Checkbox
              value={openMode === "subscription"}
              onClick={() => toggleMode("subscription")}
              isDisable={readOnly}
              color="#5856D6"
            />
            <span onClick={() => toggleMode("subscription")}>開通訂閱</span>
          </div>
        </div>

        {openMode && !isSeatPlan && optionPlans.length > 0 && (
          <div className={styles.trialPanelField}>
            <label>{modeIsTrial ? "試用方案" : "訂閱方案"}</label>
            <select
              className={styles.trialPanelSelect}
              value={selectedPlanId || ""}
              onChange={(e) => setSelectedPlanId(Number(e.target.value))}
              disabled={readOnly}
            >
              {optionPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {planOptionLabel(plan)}
                </option>
              ))}
            </select>
          </div>
        )}

        {openMode && isSeatPlan && (
          <div className={styles.trialPanelField}>
            <label>{modeIsTrial ? "試用面額" : "訂閱面額"}</label>
            <div className={styles.trialPanelSeatList}>
              {seatRows.map((row, index) => (
                <div className={styles.trialPanelSeatCard} key={`seat-row-${index}`}>
                  <div className={styles.trialPanelSeatCardTop}>
                    <span className={styles.trialPanelSeatCardTag}>
                      面額{toChineseNumber(index + 1)}
                    </span>
                    {!readOnly && (
                      <Button
                        type='pure'
                        icon={<BiTrashAlt />}
                        color='#ff766e'
                        disabled={seatRows.length === 1}
                        onClick={() => removeSeatRow(index)}
                      />
                    )}
                  </div>
                  <div className={styles.trialPanelSeatCardBody}>
                    <div className={styles.trialPanelSeatField}>
                      <span className={styles.trialPanelSeatFieldLabel}>面額</span>
                      <select
                        className={styles.trialPanelSelect}
                        value={String(row.plan_id || "")}
                        onChange={(e) =>
                          handleSeatRowChange(index, "plan_id", e.target.value)
                        }
                        disabled={readOnly}
                      >
                        {storedValuePlans.map((plan) => (
                          <option key={plan.id} value={String(plan.id)}>
                            {seatPlanLabel(plan)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.trialPanelSeatField}>
                      <span className={styles.trialPanelSeatFieldLabel}>席位人數</span>
                      <TextField
                        placeholder='請輸入'
                        width='100%'
                        numeric={true}
                        value={row.number_of_person ?? ""}
                        onChange={(v) =>
                          handleSeatRowChange(index, "number_of_person", v)
                        }
                        error={!(Number(row.number_of_person) > 0) && error}
                        min={1}
                        isDisabled={readOnly}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!readOnly && (
              <div className={styles.trialPanelSeatAdd}>
                <Button type='tertiary' title='新增面額' onClick={addSeatRow} />
              </div>
            )}
          </div>
        )}

        <div className={styles.trialPanelFoot}>
          {readOnly ? (
            <Button title='關閉' type='secondary' onClick={onClose} />
          ) : (
            <>
              <Button title='取消' type='secondary' onClick={onClose} />
              <Button title='完成' type='primary' onClick={handleComplete} />
            </>
          )}
        </div>
      </div>

      {errorMsg && (
        <Alert
          isWarning={true}
          isOpen={!!errorMsg}
          mainTitle={errorMsg}
          rightButtonText='確定'
          onConfirm={() => setErrorMsg("")}
          onClose={() => setErrorMsg("")}
        />
      )}
    </>
  );
};

export default TrialPlanPanel;
